import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, AlertCircle, BookOpen, Activity, Heart, Save, Printer, Sparkles, 
  Cpu, CheckCircle2, ShieldAlert, Info, Edit2, Trash2, Plus 
} from 'lucide-react';
import apiService from '../../services/api';
import { useTreatment } from '../../context/TreatmentContext';
import TreatmentSubHeader from '../../components/TreatmentSubHeader';
import HeaderLogo from '../../components/HeaderLogo';
import '../../styles/treatment.css';

const Recommendations = () => {
  const { currentPatient, setLastRecommendation } = useTreatment();
  const [searchTerm, setSearchTerm] = useState('');
  const [conditions, setConditions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [useAi, setUseAi] = useState(false);
  const [customNotes, setCustomNotes] = useState('');
  const [aiStatus, setAiStatus] = useState({ enabled: true, provider: 'Gemini', modelName: 'gemini-1.5-flash' });
  
  const [recommendation, setRecommendation] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const navigate = useNavigate();

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    clinicalRationale: '',
    recommendedMedicines: [],
    recommendedYogaAsanas: [],
    pathyaList: [],
    apathyaList: [],
    lifestyleModifications: [],
    warnings: []
  });

  // Builder states for list additions
  const [newMed, setNewMed] = useState({ commonName: '', sanskritName: '', dosage: '' });
  const [newYoga, setNewYoga] = useState({ asanaName: '', sanskritName: '', duration: '' });
  const [newPathya, setNewPathya] = useState('');
  const [newApathya, setNewApathya] = useState('');
  const [newLifestyle, setNewLifestyle] = useState('');
  const [newWarning, setNewWarning] = useState('');

  const startEditing = () => {
    if (!recommendation) return;
    setEditForm({
      clinicalRationale: recommendation.clinicalRationale || '',
      recommendedMedicines: recommendation.recommendedMedicines ? [...recommendation.recommendedMedicines] : [],
      recommendedYogaAsanas: recommendation.recommendedYogaAsanas ? [...recommendation.recommendedYogaAsanas] : [],
      pathyaList: recommendation.pathyaList ? [...recommendation.pathyaList] : [],
      apathyaList: recommendation.apathyaList ? [...recommendation.apathyaList] : [],
      lifestyleModifications: recommendation.lifestyleModifications ? [...recommendation.lifestyleModifications] : [],
      warnings: recommendation.warnings ? [...recommendation.warnings] : [],
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveEdits = (e) => {
    e.preventDefault();
    const updatedDietaryItems = [
      ...editForm.pathyaList.map(food => ({ foodName: food, category: 'Pathya (Recommended Diet)', benefits: '' })),
      ...editForm.apathyaList.map(food => ({ foodName: food, category: 'Apathya (Foods to Avoid)', benefits: '' }))
    ];

    setRecommendation(prev => ({
      ...prev,
      clinicalRationale: editForm.clinicalRationale,
      recommendedMedicines: editForm.recommendedMedicines,
      recommendedYogaAsanas: editForm.recommendedYogaAsanas,
      pathyaList: editForm.pathyaList,
      apathyaList: editForm.apathyaList,
      recommendedDietaryItems: updatedDietaryItems,
      lifestyleModifications: editForm.lifestyleModifications,
      warnings: editForm.warnings
    }));
    setIsEditing(false);
  };

  const addMedicine = () => {
    if (!newMed.commonName.trim()) return;
    setEditForm(prev => ({
      ...prev,
      recommendedMedicines: [...prev.recommendedMedicines, { ...newMed }]
    }));
    setNewMed({ commonName: '', sanskritName: '', dosage: '' });
  };

  const removeMedicine = (index) => {
    setEditForm(prev => ({
      ...prev,
      recommendedMedicines: prev.recommendedMedicines.filter((_, i) => i !== index)
    }));
  };

  const addYoga = () => {
    if (!newYoga.asanaName.trim()) return;
    setEditForm(prev => ({
      ...prev,
      recommendedYogaAsanas: [...prev.recommendedYogaAsanas, { ...newYoga }]
    }));
    setNewYoga({ asanaName: '', sanskritName: '', duration: '' });
  };

  const removeYoga = (index) => {
    setEditForm(prev => ({
      ...prev,
      recommendedYogaAsanas: prev.recommendedYogaAsanas.filter((_, i) => i !== index)
    }));
  };

  const addPathya = () => {
    if (!newPathya.trim()) return;
    setEditForm(prev => ({
      ...prev,
      pathyaList: [...prev.pathyaList, newPathya.trim()]
    }));
    setNewPathya('');
  };

  const removePathya = (index) => {
    setEditForm(prev => ({
      ...prev,
      pathyaList: prev.pathyaList.filter((_, i) => i !== index)
    }));
  };

  const addApathya = () => {
    if (!newApathya.trim()) return;
    setEditForm(prev => ({
      ...prev,
      apathyaList: [...prev.apathyaList, newApathya.trim()]
    }));
    setNewApathya('');
  };

  const removeApathya = (index) => {
    setEditForm(prev => ({
      ...prev,
      apathyaList: prev.apathyaList.filter((_, i) => i !== index)
    }));
  };

  const addLifestyle = () => {
    if (!newLifestyle.trim()) return;
    setEditForm(prev => ({
      ...prev,
      lifestyleModifications: [...prev.lifestyleModifications, newLifestyle.trim()]
    }));
    setNewLifestyle('');
  };

  const removeLifestyle = (index) => {
    setEditForm(prev => ({
      ...prev,
      lifestyleModifications: prev.lifestyleModifications.filter((_, i) => i !== index)
    }));
  };

  const addWarning = () => {
    if (!newWarning.trim()) return;
    setEditForm(prev => ({
      ...prev,
      warnings: [...prev.warnings, newWarning.trim()]
    }));
    setNewWarning('');
  };

  const removeWarning = (index) => {
    setEditForm(prev => ({
      ...prev,
      warnings: prev.warnings.filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    // Fetch AI Status configuration on mount
    apiService.getAiStatus()
      .then(config => {
        if (config) setAiStatus(config);
      })
      .catch(() => {
        // Fallback default if status API fails
      });
  }, []);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    setSearchError('');
    setConditions([]);
    setSelectedCondition(null);
    setRecommendation(null);
    try {
      const data = await apiService.searchConditions(searchTerm.trim());
      setConditions(Array.isArray(data) ? data : []);
    } catch (err) {
      setSearchError(err.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleGenerate = async () => {
    if (!currentPatient?.id || !selectedCondition) return;
    setGenerating(true);
    setGenError('');
    try {
      const isCustom = selectedCondition.id === 0;
      const data = await apiService.generateRecommendations(
        currentPatient.id, 
        selectedCondition.id, 
        useAi, 
        customNotes,
        isCustom ? selectedCondition.name : null
      );
      setRecommendation(data);
    } catch (err) {
      setGenError(err.message || 'Failed to generate recommendations');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveAsPlan = () => {
    if (!recommendation) return;
    setLastRecommendation(recommendation);
    navigate('/treatment/plans?fromRecommendation=1');
  };

  const renderEditForm = () => {
    return (
      <form onSubmit={handleSaveEdits} className="treatment-form" style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Modify Recommendation Parameters</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" className="btn btn-primary">Save Changes</button>
            <button type="button" className="btn btn-outline" onClick={handleCancelEdit}>Cancel</button>
          </div>
        </div>

        {/* Clinical Rationale */}
        <div className="form-group">
          <label className="form-label">Ayurvedic Pathogenesis & Clinical Strategy (Samprapti)</label>
          <textarea
            className="form-control"
            rows={4}
            value={editForm.clinicalRationale}
            onChange={(e) => setEditForm(prev => ({ ...prev, clinicalRationale: e.target.value }))}
            placeholder="Clinical rationale..."
            style={{
              background: 'rgba(10, 14, 39, 0.6)',
              border: '1px solid rgba(0, 212, 255, 0.15)',
              color: 'var(--text-main)',
              borderRadius: '8px',
              padding: '0.75rem',
            }}
          />
        </div>

        {/* Herbal Formulations */}
        <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '1rem', background: 'rgba(255,255,255,0.01)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 1rem 0' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary-color)' }}><BookOpen size={16} /> Recommended Herbal Formulations</h4>
            {editForm.recommendedMedicines.length > 0 && (
              <button type="button" onClick={() => setEditForm(prev => ({ ...prev, recommendedMedicines: [] }))} style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Trash2 size={12} /> Clear All</button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {editForm.recommendedMedicines.map((m, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>
                  <strong>{m.commonName}</strong> {m.sanskritName && `(${m.sanskritName})`} - <span style={{ color: 'var(--text-secondary)' }}>{m.dosage}</span>
                </div>
                <button type="button" className="btn btn-outline" onClick={() => removeMedicine(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem', background: 'transparent' }}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.2fr auto', gap: '0.75rem', alignItems: 'end' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <input type="text" className="form-control" placeholder="Common Name" value={newMed.commonName} onChange={e => setNewMed(prev => ({ ...prev, commonName: e.target.value }))} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <input type="text" className="form-control" placeholder="Sanskrit Name" value={newMed.sanskritName} onChange={e => setNewMed(prev => ({ ...prev, sanskritName: e.target.value }))} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <input type="text" className="form-control" placeholder="Dosage (e.g. 3g twice daily)" value={newMed.dosage} onChange={e => setNewMed(prev => ({ ...prev, dosage: e.target.value }))} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            </div>
            <button type="button" className="btn btn-primary" onClick={addMedicine} style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
          </div>
        </div>

        {/* Yoga & Pranayama */}
        <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '1rem', background: 'rgba(255,255,255,0.01)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 1rem 0' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary-color)' }}><Activity size={16} /> Recommended Yoga & Pranayama</h4>
            {editForm.recommendedYogaAsanas.length > 0 && (
              <button type="button" onClick={() => setEditForm(prev => ({ ...prev, recommendedYogaAsanas: [] }))} style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Trash2 size={12} /> Clear All</button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {editForm.recommendedYogaAsanas.map((y, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>
                  <strong>{y.asanaName}</strong> {y.sanskritName && `(${y.sanskritName})`} - <span style={{ color: 'var(--text-secondary)' }}>{y.duration}</span>
                </div>
                <button type="button" className="btn btn-outline" onClick={() => removeYoga(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem', background: 'transparent' }}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr auto', gap: '0.75rem', alignItems: 'end' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <input type="text" className="form-control" placeholder="Asana Name" value={newYoga.asanaName} onChange={e => setNewYoga(prev => ({ ...prev, asanaName: e.target.value }))} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <input type="text" className="form-control" placeholder="Sanskrit / Instructions" value={newYoga.sanskritName} onChange={e => setNewYoga(prev => ({ ...prev, sanskritName: e.target.value }))} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <input type="text" className="form-control" placeholder="Duration (e.g. 15 mins)" value={newYoga.duration} onChange={e => setNewYoga(prev => ({ ...prev, duration: e.target.value }))} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            </div>
            <button type="button" className="btn btn-primary" onClick={addYoga} style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
          </div>
        </div>

        {/* Pathya (Foods to Favor) */}
        <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '1rem', background: 'rgba(255,255,255,0.01)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 1rem 0' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981' }}><CheckCircle2 size={16} /> Pathya (Foods & Diet to Favor)</h4>
            {editForm.pathyaList.length > 0 && (
              <button type="button" onClick={() => setEditForm(prev => ({ ...prev, pathyaList: [] }))} style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Trash2 size={12} /> Clear All</button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {editForm.pathyaList.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>{item}</div>
                <button type="button" className="btn btn-outline" onClick={() => removePathya(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem', background: 'transparent' }}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <input type="text" className="form-control" placeholder="Add recommended food item..." value={newPathya} onChange={e => setNewPathya(e.target.value)} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            <button type="button" className="btn btn-primary" onClick={addPathya} style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
          </div>
        </div>

        {/* Apathya (Foods to Avoid) */}
        <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '1rem', background: 'rgba(255,255,255,0.01)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 1rem 0' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#ef4444' }}><ShieldAlert size={16} /> Apathya (Foods & Habits to Avoid)</h4>
            {editForm.apathyaList.length > 0 && (
              <button type="button" onClick={() => setEditForm(prev => ({ ...prev, apathyaList: [] }))} style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Trash2 size={12} /> Clear All</button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {editForm.apathyaList.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>{item}</div>
                <button type="button" className="btn btn-outline" onClick={() => removeApathya(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem', background: 'transparent' }}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <input type="text" className="form-control" placeholder="Add food/habit to avoid..." value={newApathya} onChange={e => setNewApathya(e.target.value)} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            <button type="button" className="btn btn-primary" onClick={addApathya} style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
          </div>
        </div>

        {/* Lifestyle Modifications */}
        <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '1rem', background: 'rgba(255,255,255,0.01)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 1rem 0' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary-color)' }}><Heart size={16} /> Dinacharya (Daily Routine) & Lifestyle</h4>
            {editForm.lifestyleModifications.length > 0 && (
              <button type="button" onClick={() => setEditForm(prev => ({ ...prev, lifestyleModifications: [] }))} style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Trash2 size={12} /> Clear All</button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {editForm.lifestyleModifications.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>{item}</div>
                <button type="button" className="btn btn-outline" onClick={() => removeLifestyle(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem', background: 'transparent' }}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <input type="text" className="form-control" placeholder="Add lifestyle guideline..." value={newLifestyle} onChange={e => setNewLifestyle(e.target.value)} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            <button type="button" className="btn btn-primary" onClick={addLifestyle} style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
          </div>
        </div>

        {/* Safety Warnings */}
        <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '1rem', background: 'rgba(255,255,255,0.01)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 1rem 0' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#f59e0b' }}><AlertCircle size={16} /> Precautions & Safety Alerts</h4>
            {editForm.warnings.length > 0 && (
              <button type="button" onClick={() => setEditForm(prev => ({ ...prev, warnings: [] }))} style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Trash2 size={12} /> Clear All</button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {editForm.warnings.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>{item}</div>
                <button type="button" className="btn btn-outline" onClick={() => removeWarning(idx)} style={{ color: 'var(--accent-pink)', border: 'none', padding: '0.25rem', background: 'transparent' }}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <input type="text" className="form-control" placeholder="Add safety warning..." value={newWarning} onChange={e => setNewWarning(e.target.value)} style={{ background: 'rgba(10, 14, 39, 0.6)', border: '1px solid rgba(0, 212, 255, 0.15)', color: 'var(--text-main)' }} />
            <button type="button" className="btn btn-primary" onClick={addWarning} style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
          <button type="submit" className="btn btn-primary">Save Changes</button>
          <button type="button" className="btn btn-outline" onClick={handleCancelEdit}>Cancel</button>
        </div>
      </form>
    );
  };

  if (!currentPatient) {
    return (
      <div className="treatment-page">
        <section className="hero">
          <div className="container">
            <div className="hero-content text-center">
              <h1>Treatment Recommendations</h1>
              <p>Get personalized AYUSH recommendations based on condition and Prakriti.</p>
            </div>
          </div>
        </section>
        <TreatmentSubHeader />
        <section className="section">
          <div className="container">
            <div className="treatment-card treatment-card-cta">
              <AlertCircle size={24} />
              <div>
                <h3>Select a patient first</h3>
                <p>Choose a patient to generate recommendations.</p>
                <Link to="/treatment/patients" className="btn btn-primary">Go to Patients</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="treatment-page">
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1>Treatment Recommendations</h1>
            <p>
              Search for a condition and get personalized herbal, yoga, and dietary recommendations for{' '}
              <strong>{currentPatient.name}</strong>
              {currentPatient.prakriti && ` (${currentPatient.prakriti})`}.
            </p>
          </div>
        </div>
      </section>
      <TreatmentSubHeader />

      <section className="section">
        <div className="container">
          <div className="treatment-breadcrumb">
            <Link to="/treatment">AYUSH Care</Link>
            <span> / Recommendations</span>
          </div>

          {/* AI Feature Control Panel */}
          <div className="treatment-card ai-feature-banner" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.08) 0%, rgba(124, 58, 237, 0.08) 100%)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ padding: '0.6rem', borderRadius: '12px', background: 'rgba(0, 212, 255, 0.15)', color: 'var(--primary-color)' }}>
                  <Sparkles size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    Vedic AI Clinical Engine
                    <span className="badge badge-accent" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}>
                      {aiStatus?.provider || 'Gemini'}
                    </span>
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                    Feature-flagged dynamic Prakriti reasoning with grounded classical knowledge retrieval.
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: useAi ? 'var(--primary-color)' : 'var(--text-secondary)' }}>
                  {useAi ? '✨ AI Engine Enabled' : '📊 Rule-Based Engine'}
                </span>
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px' }}>
                  <input 
                    type="checkbox" 
                    checked={useAi} 
                    onChange={(e) => setUseAi(e.target.checked)} 
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: useAi ? 'var(--primary-color)' : '#444',
                    borderRadius: '24px', transition: '0.3s'
                  }}>
                    <span style={{
                      position: 'absolute', content: '""', height: '18px', width: '18px', left: useAi ? '26px' : '3px', bottom: '3px',
                      backgroundColor: '#white', borderRadius: '50%', transition: '0.3s', background: '#fff'
                    }}></span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {!recommendation ? (
            <>
              {/* Search Condition Card */}
              <div className="treatment-card" style={{ marginBottom: '1.5rem' }}>
                <div style={{ flex: 1 }}>
                  <h3>1. Search Diagnosed Condition</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. osteoarthritis, diabetes, hypertension, asthma"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      style={{ flex: 1, minWidth: '200px' }}
                    />
                    <button className="btn btn-primary" onClick={handleSearch} disabled={searching}>
                      <Search size={18} /> {searching ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                  {searchError && (
                    <p style={{ marginTop: '0.5rem', color: 'var(--accent-pink)', fontSize: '0.9rem' }}>
                      <AlertCircle size={16} /> {searchError}
                    </p>
                  )}
                </div>
              </div>

              {/* Condition Selection & Clinical Notes Card */}
              {(conditions.length > 0 || (searchTerm.trim() && !searching)) && (
                <div className="treatment-card" style={{ marginBottom: '1.5rem', flexDirection: 'column', gap: '1.25rem' }}>
                  <div>
                    <h3>2. Select Diagnosed Condition</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
                      {conditions.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="btn btn-outline"
                          style={{
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                            borderColor: selectedCondition?.id === c.id ? 'var(--primary-color)' : undefined,
                            background: selectedCondition?.id === c.id ? 'rgba(0, 212, 255, 0.08)' : undefined,
                          }}
                          onClick={() => setSelectedCondition(c)}
                        >
                          <strong>{c.name}</strong>
                          {c.sanskritName && (
                            <span style={{ color: 'var(--text-muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                              ({c.sanskritName})
                            </span>
                          )}
                        </button>
                      ))}

                      {conditions.length === 0 && searchTerm.trim() && (
                        <button
                          type="button"
                          className="btn btn-outline"
                          style={{
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                            borderColor: selectedCondition?.id === 0 ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.1)',
                            background: selectedCondition?.id === 0 ? 'rgba(0, 212, 255, 0.08)' : undefined,
                            borderStyle: 'dashed'
                          }}
                          onClick={() => setSelectedCondition({
                            id: 0,
                            name: searchTerm.trim(),
                            sanskritName: 'Vyadhi'
                          })}
                        >
                          <strong>✨ Use Custom Condition: "{searchTerm.trim()}"</strong>
                          <span style={{ color: 'var(--text-muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                            (Saves to clinical database upon generation)
                          </span>
                        </button>
                      )}
                    </div>
                  </div>

                  {selectedCondition && (
                    <div style={{ paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>3. Optional Doctor Observations / Patient Symptoms</h4>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="Add specific observations e.g. Patient has high digestive fire (Tikshnagni) and knee stiffness in morning..."
                        value={customNotes}
                        onChange={(e) => setCustomNotes(e.target.value)}
                        style={{ width: '100%', marginBottom: '1rem' }}
                      />

                      <button
                        className="btn btn-primary"
                        onClick={handleGenerate}
                        disabled={generating}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        {generating ? (
                          <> <Cpu className="animate-spin" size={18} /> Generating Clinical Plan... </>
                        ) : (
                          <> <Sparkles size={18} /> Generate {useAi ? 'Vedic AI' : 'Rule-Based'} Recommendations </>
                        )}
                      </button>
                    </div>
                  )}

                  {genError && (
                    <p style={{ marginTop: '0.5rem', color: 'var(--accent-pink)', fontSize: '0.9rem' }}>
                      <AlertCircle size={16} /> {genError}
                    </p>
                  )}
                </div>
              )}

              {!searching && conditions.length === 0 && searchTerm && (
                <div className="treatment-empty">
                  <p>No matching conditions found. Try searching for "Kidney", "Diabetes", or "Joint".</p>
                </div>
              )}
            </>
          ) : (
            /* Recommendations Output Container */
            isEditing ? (
              renderEditForm()
            ) : (
              <div className="treatment-card" style={{ flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '1rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <h2 style={{ margin: 0 }}>{recommendation.conditionName}</h2>
                      <span className={`badge ${recommendation.isAiGenerated ? 'badge-accent' : 'badge-neutral'}`}>
                        {recommendation.isAiGenerated ? `✨ AI Engine (${recommendation.aiModelUsed})` : '📊 Rule-Based Engine'}
                      </span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                      Patient: <strong>{recommendation.patientName}</strong> · Prakriti: <span className="badge badge-outline">{recommendation.prakriti}</span>
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ textAlign: 'right', marginRight: '0.5rem' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Clinical Match</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                        {Number(recommendation.confidenceScore).toFixed(1)}%
                      </div>
                    </div>
                    <button className="btn btn-outline" onClick={startEditing}>
                      <Edit2 size={18} /> Edit Plan
                    </button>
                    <button className="btn btn-outline" onClick={() => window.print()}>
                      <Printer size={18} /> Print
                    </button>
                    <button className="btn btn-primary" onClick={handleSaveAsPlan}>
                      <Save size={18} /> Save Plan
                    </button>
                  </div>
                </div>

              {/* AI Clinical Pathogenesis / Rationale Card */}
              {recommendation.clinicalRationale && (
                <div style={{ background: 'rgba(0, 212, 255, 0.05)', borderRadius: '12px', padding: '1.25rem', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary-color)' }}>
                    <Info size={20} /> Ayurvedic Pathogenesis & Clinical Strategy (Samprapti)
                  </h3>
                  <p style={{ color: 'var(--text-main)', whiteSpace: 'pre-wrap', lineHeight: '1.6', margin: 0 }}>
                    {recommendation.clinicalRationale}
                  </p>
                </div>
              )}

              {/* Herbal Medicines */}
              {recommendation.recommendedMedicines?.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1.2rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <BookOpen size={20} /> Recommended Herbal Formulations
                  </h3>
                  <div className="recommendation-category-grid">
                    {recommendation.recommendedMedicines.map((m, idx) => (
                      <div key={m.id || idx} className="recommendation-item-card recommendation-item-card--herb">
                        <div className="recommendation-item-title">{m.commonName}</div>
                        {m.sanskritName && <div className="recommendation-item-subtitle">{m.sanskritName}</div>}
                        {m.dosage && <div className="recommendation-item-desc"><strong>Dosage:</strong> {m.dosage}</div>}
                        {recommendation.anupanaInstructions && recommendation.anupanaInstructions[m.commonName] && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--accent-pink)', background: 'rgba(255, 0, 128, 0.08)', padding: '0.3rem 0.5rem', borderRadius: '6px' }}>
                            <strong>Anupana (Medium):</strong> {recommendation.anupanaInstructions[m.commonName]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Yoga & Pranayama */}
              {recommendation.recommendedYogaAsanas?.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1.2rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Activity size={20} /> Recommended Yoga & Pranayama
                  </h3>
                  <div className="recommendation-category-grid">
                    {recommendation.recommendedYogaAsanas.map((a, idx) => (
                      <div key={a.id || idx} className="recommendation-item-card recommendation-item-card--yoga">
                        <div className="recommendation-item-title">{a.asanaName}</div>
                        {a.sanskritName && <div className="recommendation-item-subtitle">{a.sanskritName}</div>}
                        {a.duration && <div className="recommendation-item-desc"><strong>Duration:</strong> {a.duration}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pathya (Favor) vs Apathya (Avoid) Dietary Cards */}
              {(recommendation.pathyaList?.length > 0 || recommendation.apathyaList?.length > 0) && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                  {recommendation.pathyaList?.length > 0 && (
                    <div style={{ background: 'rgba(16, 185, 129, 0.08)', borderRadius: '12px', padding: '1rem', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <CheckCircle2 size={18} /> Pathya (Foods & Diet to Favor)
                      </h4>
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                        {recommendation.pathyaList.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}

                  {recommendation.apathyaList?.length > 0 && (
                    <div style={{ background: 'rgba(239, 68, 68, 0.08)', borderRadius: '12px', padding: '1rem', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <ShieldAlert size={18} /> Apathya (Foods & Habits to Avoid)
                      </h4>
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                        {recommendation.apathyaList.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Lifestyle Modifications */}
              {recommendation.lifestyleModifications?.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1.2rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Heart size={20} /> Dinacharya (Daily Routine) & Lifestyle
                  </h3>
                  <div className="recommendation-category-grid">
                    {recommendation.lifestyleModifications.map((item, i) => (
                      <div key={i} className="recommendation-item-card recommendation-item-card--lifestyle">
                        <div className="recommendation-item-desc">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Safety Warnings */}
              {recommendation.warnings?.length > 0 && (
                <div style={{ background: 'rgba(245, 158, 11, 0.08)', borderRadius: '10px', padding: '0.85rem 1rem', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#f59e0b', fontSize: '0.9rem' }}>
                  <strong>Precautions & Safety Alerts:</strong>
                  <ul style={{ margin: '0.25rem 0 0 0', paddingLeft: '1.2rem' }}>
                    {recommendation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }} className="no-print">
                <button className="btn btn-outline" onClick={() => setRecommendation(null)}>
                  Generate for another condition
                </button>
                <Link to="/treatment" className="btn btn-outline">Back to dashboard</Link>
              </div>
            </div>
          )
        )}
        </div>
      </section>

      {/* PRINT ONLY CLINICAL DRAFT PLAN */}
      {recommendation && (
        <div className="print-only" style={{
          color: '#000',
          background: '#fff',
          padding: '2rem',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          lineHeight: '1.6',
        }}>
          {/* Redesigned Integrated Header */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
            {/* Logo and Subtitle Section */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <HeaderLogo />
                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#000', letterSpacing: '0.5px' }}>
                    Vedic <span style={{ color: '#7C5CFC' }}>Surgery</span>
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Shalya Tantra · AI</span>
                </div>
              </div>
            </div>
            
            {/* Patient Profile & Record Metadata Box */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem', background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ textAlign: 'left' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Patient Case Profile</h4>
                <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Name:</strong> {recommendation.patientName}</p>
                <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Constitutional Prakriti:</strong> {recommendation.prakriti}</p>
              </div>
              <div style={{ textAlign: 'left' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Clinical Record Metadata</h4>
                <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Lead Clinician:</strong> Dr. Vijay Pratap Singh</p>
                <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Last Checkup:</strong> {new Date().toLocaleDateString()}</p>
                <p style={{ margin: '0.2rem 0', fontSize: '0.9rem' }}><strong>Next Scheduled Checkup:</strong> N/A</p>
              </div>
            </div>
          </div>

          {/* Condition & Rationale */}
          <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
            <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Diagnosed Condition & Samprapti Rationale</h2>
            <p style={{ margin: '0.5rem 0' }}><strong>Condition:</strong> {recommendation.conditionName}</p>
            <p style={{ margin: '0.5rem 0' }}><strong>Duration of Plan:</strong> 3 months</p>
            <p style={{ margin: '0.5rem 0', whiteSpace: 'pre-wrap' }}><strong>Ayurvedic Clinical Pathophysiology (*Samprapti*):</strong> {recommendation.clinicalRationale}</p>
          </div>

          {/* Herbal Formulations */}
          {recommendation.recommendedMedicines && recommendation.recommendedMedicines.length > 0 && (
            <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
              <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Herbal Formulations & Prescriptions</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #000' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Formulation Name</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Sanskrit Name</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Dosage & Frequency</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendation.recommendedMedicines.map((m, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.5rem' }}>{m.commonName}</td>
                      <td style={{ padding: '0.5rem' }}>{m.sanskritName || 'N/A'}</td>
                      <td style={{ padding: '0.5rem' }}>{m.dosage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Yoga & Pranayama */}
          {recommendation.recommendedYogaAsanas && recommendation.recommendedYogaAsanas.length > 0 && (
            <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
              <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Yoga & Pranayama Guidelines</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #000' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Asana Name</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Sanskrit Name</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Daily Duration / Guidance</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendation.recommendedYogaAsanas.map((y, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.5rem' }}>{y.asanaName}</td>
                      <td style={{ padding: '0.5rem' }}>{y.sanskritName || 'N/A'}</td>
                      <td style={{ padding: '0.5rem' }}>{y.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Dietary Prescriptions (Pathya & Apathya) */}
          {((recommendation.pathyaList && recommendation.pathyaList.length > 0) || (recommendation.apathyaList && recommendation.apathyaList.length > 0)) && (
            <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
              <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Dietary Prescriptions (Pathya & Apathya)</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #000' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Food / Ingredient</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendation.pathyaList && recommendation.pathyaList.map((item, idx) => (
                    <tr key={`pathya-${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.5rem' }}>{item}</td>
                      <td style={{ padding: '0.5rem', color: '#10b981' }}>Pathya (Recommended Diet)</td>
                    </tr>
                  ))}
                  {recommendation.apathyaList && recommendation.apathyaList.map((item, idx) => (
                    <tr key={`apathya-${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.5rem' }}>{item}</td>
                      <td style={{ padding: '0.5rem', color: '#ef4444' }}>Apathya (Foods to Avoid)</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Dinacharya */}
          {recommendation.lifestyleModifications && recommendation.lifestyleModifications.length > 0 && (
            <div style={{ marginBottom: '2rem', textAlign: 'left' }}>
              <h2 style={{ fontSize: '1.3rem', borderBottom: '1px solid #000', paddingBottom: '0.3rem', color: '#000', textTransform: 'uppercase' }}>Dinacharya (Daily Routine) & Lifestyle</h2>
              <ul style={{ paddingLeft: '1.2rem', margin: '0.5rem 0' }}>
                {recommendation.lifestyleModifications.map((item, idx) => (
                  <li key={idx} style={{ margin: '0.25rem 0' }}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer Signatures */}
          <div style={{ marginTop: '5rem', display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '0.5rem' }}>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>Patient Signature</p>
            </div>
            <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '0.5rem' }}>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>Authorized Clinician</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recommendations;
